import {type MessageDescriptor} from '@lingui/core'
import {msg} from '@lingui/core/macro'
import {nanoid} from 'nanoid/non-secure'

import {type Slide, type SlideType} from '#/types/capsule'

/**
 * The 13 supported block/slide types shown in the "+" picker of both the
 * CapsuleStudio and the FlashBookStudio.
 */
export const SLIDE_TYPES_PICKER: {
  type: SlideType
  label: MessageDescriptor
  desc: MessageDescriptor
  icon: string
  color: string
}[] = [
  {
    type: 'single_choice',
    label: msg`Single choice`,
    desc: msg`Only one correct answer`,
    icon: 'check-circle',
    color: '#0085ff',
  },
  {
    type: 'multiple_choice',
    label: msg`Multiple choice`,
    desc: msg`Several correct answers`,
    icon: 'check-square',
    color: '#1d9bf0',
  },
  {
    type: 'true_false',
    label: msg`True or False`,
    desc: msg`Binary answer`,
    icon: 'toggle-left',
    color: '#22c55e',
  },
  {
    type: 'fill_blanks',
    label: msg`Fill in the blanks`,
    desc: msg`Blanks in a paragraph`,
    icon: 'edit-2',
    color: '#d97706',
  },
  {
    type: 'matching',
    label: msg`Matching`,
    desc: msg`Connect the pairs`,
    icon: 'shuffle',
    color: '#a855f7',
  },
  {
    type: 'ordering',
    label: msg`Ranking`,
    desc: msg`Put items in order`,
    icon: 'list',
    color: '#ec4899',
  },
  {
    type: 'image_choice',
    label: msg`Image choice`,
    desc: msg`Choose from images`,
    icon: 'image',
    color: '#14b8a6',
  },
  {
    type: 'accordion',
    label: msg`Accordion`,
    desc: msg`Expandable sections`,
    icon: 'chevrons-down',
    color: '#f97316',
  },
  {
    type: 'drag_words',
    label: msg`Drag words`,
    desc: msg`Fill blanks with word bank`,
    icon: 'move',
    color: '#8b5cf6',
  },
  {
    type: 'find_words',
    label: msg`Find words`,
    desc: msg`Spot the correct words`,
    icon: 'search',
    color: '#06b6d4',
  },
  {
    type: 'mark_words',
    label: msg`Mark words`,
    desc: msg`Highlight specific words`,
    icon: 'underline',
    color: '#d946ef',
  },
  {
    type: 'complex_fill_blank',
    label: msg`Complex fill blank`,
    desc: msg`Multiple dropdown blanks`,
    icon: 'sliders',
    color: '#10b981',
  },
  {
    type: 'content',
    label: msg`Content`,
    desc: msg`Text or explanatory content`,
    icon: 'layout',
    color: '#6b8299',
  },
]

/**
 * Creates a new block/slide with sensible defaults for the given type.
 *
 * `patch` seeds the type-specific fields a caller already knows — the FlashBook
 * studio uses it to create a `content` block that is a quote, a note or a table
 * straight from the picker, instead of creating a plain block and mutating it in
 * a second render.
 */
export function createSlide(type: SlideType, patch?: Partial<Slide>): Slide {
  const id = nanoid(8)
  const defaultOptions = [
    {id: 'o_0', label: '', isCorrect: true},
    {id: 'o_1', label: '', isCorrect: false},
  ]
  return {
    id,
    type,
    question: '',
    ...(type === 'single_choice' ||
    type === 'multiple_choice' ||
    type === 'image_choice'
      ? {options: defaultOptions}
      : {}),
    ...(type === 'true_false' ? {answer: 'true'} : {}),
    ...(type === 'fill_blank'
      ? {blanks: [{id: 'b_0', before: '', answer: '', after: ''}]}
      : {}),
    ...(type === 'matching'
      ? {
          pairs: [
            {id: 'p_0', left: '', right: ''},
            {id: 'p_1', left: '', right: ''},
          ],
        }
      : {}),
    ...(type === 'ordering'
      ? {
          orderItems: [
            {id: 'oi_0', label: ''},
            {id: 'oi_1', label: ''},
          ],
          correctOrder: ['oi_0', 'oi_1'],
        }
      : {}),
    ...(type === 'accordion'
      ? {accordionItems: [{id: 'ai_0', title: '', body: ''}]}
      : {}),
    ...(type === 'drag_words'
      ? {dragWordsTemplate: '', dragWordsBank: []}
      : {}),
    ...(type === 'find_words'
      ? {findWordsTargets: [], findWordsGrid: []}
      : {}),
    ...(type === 'mark_words'
      ? {markWordsText: '', markWordsCorrect: []}
      : {}),
    ...(type === 'fill_blanks'
      ? {fillBlanksParagraph: '', fillBlanksAnswers: []}
      : {}),
    ...(type === 'complex_fill_blank'
      ? {
          complexBlanks: [
            {
              id: 'cb_0',
              sentence: '',
              answer: '',
              options: [],
              alternatives: [],
            },
          ],
        }
      : {}),
    ...(patch ?? {}),
  }
}